import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Iconify from '../../components/Iconify';

// ----------------------------------------------------------------------

// 우선순위 피라미드 그래픽
function PriorityPyramid({ isPriorityPeriod, isWaitlistPeriod }) {
  return (
    <Box sx={{ py: 2 }}>
      <Stack spacing={1} alignItems="center">
        {/* 1순위 - 우선 매칭 */}
        <Box
          sx={{
            width: '60%',
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'success.main',
            textAlign: 'center',
            position: 'relative',
            opacity: isWaitlistPeriod ? 0.4 : 1,
            border: isPriorityPeriod ? '2px solid white' : 'none',
            boxShadow: isPriorityPeriod ? '0 0 0 2px rgba(255,255,255,0.3)' : 'none',
          }}
        >
          {isPriorityPeriod && (
            <Chip
              label="현재"
              size="small"
              sx={{
                position: 'absolute',
                top: -10,
                right: 16,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: 'white',
                color: 'success.main',
              }}
            />
          )}
          <Typography variant="subtitle2" sx={{ color: 'white' }}>
            1순위
          </Typography>
          <Typography variant="overline" display="block" sx={{ color: 'white'}}>
            우선 매칭 시간 내 수락
          </Typography>
        </Box>

        {/* 2순위 - 우선 매칭 이후 */}
        <Box
          sx={{
            width: '80%',
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'warning.main',
            textAlign: 'center',
            position: 'relative',
            border: isWaitlistPeriod ? '2px solid white' : 'none',
            boxShadow: isWaitlistPeriod ? '0 0 0 2px rgba(255,255,255,0.3)' : 'none',
          }}
        >
          {isWaitlistPeriod && (
            <Chip
              label="현재"
              size="small"
              sx={{
                position: 'absolute',
                top: -10,
                right: 16,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: 'white',
                color: 'warning.main',
              }}
            />
          )}
          <Typography variant="subtitle2" sx={{ color: 'white' }}>
            2순위
          </Typography>
          <Typography variant="overline" display="block" sx={{ color: 'white'}}>
            우선 매칭 이후 수락
          </Typography>
        </Box>

        {/* 마감 */}
        <Box
          sx={{
            width: '100%',
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'grey.500',
            textAlign: 'center',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'white' }}>
            최종 마감 이후
          </Typography>
          <Typography variant="overline" display="block" sx={{ color: 'white'}}>
            응답 불가
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

// ----------------------------------------------------------------------

export default function OfferDeadlineGuideDialog({ isOpen, close, isPriorityPeriod = false, isWaitlistPeriod = false }) {
  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          응답 마감 안내
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          먼저 수락한 크루부터 회사에 전달돼요.
        </Typography>

        {/* 우선순위 피라미드 */}
        <PriorityPyramid isPriorityPeriod={isPriorityPeriod} isWaitlistPeriod={isWaitlistPeriod} />

        {/* 상세 설명 */}
        <Stack spacing={2.5} sx={{ mt: 3 }}>
          {/* 우선 매칭 설명 */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
              border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Iconify icon="eva:flash-fill" sx={{ width: 18, height: 18, color: 'success.main' }} />
              <Typography variant="subtitle2" sx={{ color: 'success.dark' }}>
                1순위. 우선 매칭 (약 6시간)
              </Typography>
            </Stack>
            <Stack spacing={0.5} sx={{ pl: 3.25 }}>
              <Typography variant="body2">
                이 시간 안에 수락하면 <b>우선 매칭</b>이 진행돼요.
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                단, 먼저 수락한 크루가 많으면 대기할 수 있어요. 대기자 중 가장 높은 순위예요.
              </Typography>
            </Stack>
          </Box>

          {/* 우선 매칭 이후 설명 */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
              border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Iconify icon="eva:clock-outline" sx={{ width: 18, height: 18, color: 'warning.main' }} />
              <Typography variant="subtitle2" sx={{ color: 'warning.dark' }}>
                2순위. 우선 매칭 이후 (~24시간)
              </Typography>
            </Stack>
            <Stack spacing={0.5} sx={{ pl: 3.25 }}>
              <Typography variant="body2">
                수락할 수 있지만, 우선 매칭 시간에 수락한 크루보다 순위가 낮아요.
              </Typography>
            </Stack>
          </Box>

          {/* 마감 후 설명 */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              backgroundColor: 'grey.100',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Iconify icon="eva:close-circle-outline" sx={{ width: 18, height: 18, color: 'text.disabled' }} />
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                최종 마감 이후
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary', pl: 3.25 }}>
              미응답 처리되어 해당 제안에 응답할 수 없어요.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 3 } }}>
        <Button
          onClick={close}
          variant="contained"
          size="large"
          fullWidth
        >
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
}
